import "reflect-metadata";
import {injectable} from "inversify";
import api, {parseServerError} from "@dropDesk/data/clients/http.client";
import {CompanyEntity} from "@dropDesk/domain/entities/company/company.entity";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {ChatIntegrationConnectionEntity} from "@dropDesk/domain/entities/chat/chat_integration_connection.entity";
import {PaymentStatus} from "@dropDesk/domain/entities/payment/payment.enum";
import {ListPaginationEntity} from "@dropDesk/domain/entities/common/list_pagination.entity";
import {PaymentEntity} from "@dropDesk/domain/entities/payment/payment.entity";
import {BaileysVersionEntity} from "@dropDesk/domain/entities/chat/baileys_version.entity";


@injectable()
export abstract class PanelAdmRemoteDataSource {
    public abstract getCompanyBySearch(search: string): Promise<{ company: CompanyEntity, user: UserEntity }>;

    public abstract removeCompanyCache(idCompany: string): Promise<void>;

    public abstract allowSubscriptionUpgrade(idCompany: string, idSubscription: string): Promise<void>;

    public abstract chatConnectionState(idCompany: string): Promise<ChatIntegrationConnectionEntity>;

    public abstract listPayments(page: number, limit: number, idCompany: string, status?: PaymentStatus): Promise<ListPaginationEntity<PaymentEntity>>;

    public abstract refund(idExternal: string, id: string): Promise<void>;

    public abstract externallyPay(idExternal: string, id: string): Promise<void>;

    public abstract getBaileysVersion(): Promise<BaileysVersionEntity>;

    public abstract updateBaileysVersion(version: string): Promise<BaileysVersionEntity>;
}

@injectable()
export class PanelAdmRemoteDataSourceImpl implements PanelAdmRemoteDataSource {

    constructor() {
    }

    basePanelUrl: string = 'panel/';
    baseGetCompanyBySearch: string = `${this.basePanelUrl}getCompany`;
    baseRemoveCompanyCache: string = `${this.basePanelUrl}removeCompanyCache`;
    baseAllowSubscriptionUpgrade: string = `${this.basePanelUrl}allowSubscriptionUpgrade`;
    baseChatConnectionState: string = `${this.basePanelUrl}chatConnectionState`;
    baseListPayments: string = `${this.basePanelUrl}payments`;
    baseRefundPayment: string = `${this.basePanelUrl}refund`;
    baseExternallyPay: string = `${this.basePanelUrl}externallyPay`;
    baseBaileysVersion: string = `${this.basePanelUrl}baileysVersion`;


    public getCompanyBySearch(search: string): Promise<{ company: CompanyEntity, user: UserEntity }> {
        return new Promise<{ company: CompanyEntity, user: UserEntity }>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.baseGetCompanyBySearch}?search=${search}`);
                return resolve({
                    company: CompanyEntity.fromJson(response.data),
                    user: UserEntity.fromJson(response.data.users[0]),
                });
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public removeCompanyCache(idCompany: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.post(`${this.baseRemoveCompanyCache}`, {idCompany});
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public allowSubscriptionUpgrade(idCompany: string, idSubscription: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.post(`${this.baseAllowSubscriptionUpgrade}`, {idCompany, idSubscription});
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public async chatConnectionState(idCompany: string): Promise<ChatIntegrationConnectionEntity> {
        return new Promise<ChatIntegrationConnectionEntity>(async (resolve, reject) => {
            try {
                const response = await api.get<ChatIntegrationConnectionEntity>(`${this.baseChatConnectionState}?idCompany=${idCompany}`);
                const result = response.data;
                const chatConnection = ChatIntegrationConnectionEntity.fromJson(result);
                return resolve(chatConnection);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public async listPayments(page: number, limit: number, idCompany: string, status?: PaymentStatus): Promise<ListPaginationEntity<PaymentEntity>> {
        return new Promise<ListPaginationEntity<PaymentEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.baseListPayments}?page=${page ?? ''}&limit=${limit ?? ''}&idCompany=${idCompany}&status=${status ?? ''}`);
                const result = new ListPaginationEntity<PaymentEntity>({
                    page,
                    limit,
                    totalRows: response.data.totalRows,
                    pages: response.data.pages,
                    data: response.data.data.map((entry: PaymentEntity) => PaymentEntity.fromJson(entry))
                });
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public refund(idExternal: string, id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.post(`${this.baseRefundPayment}`, {idExternal, id});
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public externallyPay(idExternal: string, id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.post(`${this.baseExternallyPay}`, {idExternal, id});
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        })
    }

    public getBaileysVersion(): Promise<BaileysVersionEntity> {
        return new Promise<BaileysVersionEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.baseBaileysVersion}`);
                return resolve(BaileysVersionEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public updateBaileysVersion(version: string): Promise<BaileysVersionEntity> {
        return new Promise<BaileysVersionEntity>(async (resolve, reject) => {
            try {
                const response = await api.post(`${this.baseBaileysVersion}`, {version});
                return resolve(BaileysVersionEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

}
